










































import OtInviteUserDialog, { InviteUserDialogParams } from '@/areas/invitations/ot-invite-user-dialog.vue';
import { AconexImportDialogParams } from '@/components/documents/aconex-import-dialog/aconex-import-dialog-models';
import {
  AddEditDialogMode,
  AddEditDocumentDialogParams,
} from '@/components/documents/add-edit-document-dialog/add-edit-document-dialog-models';
import { SelectDocumentDialogParams } from '@/components/documents/select-document-dialog/select-document-dialog-models';
import OtStandardHeaderArchetype from '@/components/global/archetypes/ot-standard-header-archetype.vue';
import { IInputAction, IInputTab } from '@/components/global/common-models';
import OtButton from '@/components/global/ot-button.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtTabs from '@/components/global/ot-tabs.vue';
import OtTag, { TagStatus } from '@/components/global/ot-tag.vue';
import OtRequestContractDialog from '@/components/specific-modals/ot-request-contract-dialog.vue';
import { doAconexImport } from '@/services/aconex.service';
import OtApi, { executeApi } from '@/services/api.service';
import { PostUpdateDocumentAssociationsRequestModel } from '@/services/generated/api';
import { vxm } from '@/store';
import { OtProjectStatus, OtStatusType, OtUserStatus } from '@/types/status-enums';
import { areSame } from '@/utils/string-utils';
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { SegmentInstance } from '../claims/claims-models';
import { ROUTE_WORKFLOW_ADD } from '../claims/claims-routes';
import { Contract } from './contracts/contract-models';
import { ROUTE_CONTRACT_ADD } from './contracts/contract-routes';
import { ProjectDetails } from './project-models';
import {
  ROUTE_PROJECT,
  ROUTE_PROJECT_CONTRACTS,
  ROUTE_PROJECT_CREATE,
  ROUTE_PROJECT_DETAILS,
  ROUTE_PROJECT_DOCUMENTS,
  ROUTE_PROJECT_USERS,
  ROUTE_PROJECT_WORKFLOWS,
} from './projects-routes';
import { ProjectUser } from './users/project-user-models';

@Component({
  components: {
    OtStandardHeaderArchetype,
    OtTabs,
    OtLoadingSpinner,
    OtButton,
    OtTag,
    OtInviteUserDialog,
    OtRequestContractDialog,
  },
})
export default class OtProjectsLayout extends Vue {
  // * PROPS

  // * REFS
  @Ref('requestContractDialogRef')
  private readonly requestContractDialogRef!: OtRequestContractDialog;

  // * DATA

  private get newClaimButton(): IInputAction {
    return {
      actionName: 'New Workflow',
      action: {
        name: ROUTE_WORKFLOW_ADD,
        query: { project: this.projectGid },
      },
    };
  }

  private newProjectButton: IInputAction = {
    actionName: 'New Project',
    action: { name: ROUTE_PROJECT_CREATE },
  };

  private newContractButton: IInputAction = {
    actionName: 'New Contract',
    action: { name: ROUTE_CONTRACT_ADD },
  };

  private get requestContractButton(): IInputAction {
    return {
      actionName: 'Request Contract',
      action: () => {
        this.requestContractDialogRef.open({
          projectGid: this.projectGid,
        });
        // no need to await the result and check the didRequest, the contracts are not visible anywhere so we don't need to reload the list
      },
    };
  }

  private newUserButton: IInputAction = {
    actionName: 'New User',
    action: () => this.createNewUser(),
  };
  private newDocumentButton: IInputAction = {
    actionName: 'New Document',
    action: () => this.createNewDocument(),
  };
  private importDocumentButton: IInputAction = {
    actionName: 'Add Existing Document',
    action: () => this.importDocument(),
  };
  private importFromAconexButton: IInputAction = {
    actionName: 'Import From Aconex',
    action: () => this.importFromAconex(),
  };

  private get projectsTabs(): IInputTab[] {
    return [
      {
        tabId: 0,
        tabText: 'Contracts',
        tabRoute: {
          params: { projectGid: this.projectGid },
          name: ROUTE_PROJECT_CONTRACTS,
        },
      },
      {
        tabId: 1,
        tabText: 'Workflows',
        tabRoute: {
          params: { projectGid: this.projectGid },
          name: ROUTE_PROJECT_WORKFLOWS,
        },
      },
      {
        tabId: 2,
        tabText: 'Project Details',
        tabRoute: {
          params: { projectGid: this.projectGid },
          name: ROUTE_PROJECT_DETAILS,
        },
      },
      {
        tabId: 3,
        tabText: 'Documents',
        tabRoute: {
          params: { projectGid: this.projectGid },
          name: ROUTE_PROJECT_DOCUMENTS,
        },
      },
      {
        tabId: 4,
        tabText: 'Users',
        tabRoute: {
          params: { projectGid: this.projectGid },
          name: ROUTE_PROJECT_USERS,
        },
      },
    ];
  }

  private api = new OtApi();
  private loading = false;
  private project: ProjectDetails | null = null;
  private contracts: Contract[] = [];
  private projectUsers: ProjectUser[] = [];
  private segmentInstances: SegmentInstance[] = [];

  // * COMPUTED
  private get projectGid() {
    return this.$route.params.projectGid;
  }

  private get projectName() {
    return this.project?.name || '';
  }

  private get routePath(): string | null {
    return this.$route.path || null;
  }
  private get currentRouteName() {
    return this.$route.name;
  }

  private get primaryContextBtn() {
    switch (this.currentRouteName) {
      case ROUTE_PROJECT:
        return this.newProjectButton;
      case ROUTE_PROJECT_DETAILS:
        return this.newClaimButton;
      case ROUTE_PROJECT_USERS:
        return this.newUserButton;
      case ROUTE_PROJECT_CONTRACTS:
        if (vxm.userProfile.userProfile?.canCreateContract) {
          return this.newContractButton;
        } else {
          return this.requestContractButton;
        }
      case ROUTE_PROJECT_WORKFLOWS:
        return this.newClaimButton;
      case ROUTE_PROJECT_DOCUMENTS:
        return this.newDocumentButton;

      default:
        console.warn(
          `OtProjectLayout.primaryContextBtn - no primary button defined for '${this.currentRouteName}' - defaulting to newClaimButton`,
        );
        return this.newClaimButton;
    }
  }
  private get secondaryContextBtns() {
    switch (this.currentRouteName) {
      case ROUTE_PROJECT:
        return [];
      case ROUTE_PROJECT_DETAILS:
        return [];
      case ROUTE_PROJECT_USERS:
        return [];
      case ROUTE_PROJECT_CONTRACTS:
        return [];
      case ROUTE_PROJECT_WORKFLOWS:
        return [];
      case ROUTE_PROJECT_DOCUMENTS:
        return [this.importDocumentButton, this.importFromAconexButton];
      default:
        console.warn(
          `OtSettingsLayout.secondaryContextBtns - no secondary buttons defined for '${this.currentRouteName}' - defaulting to []`,
        );
        return [];
    }
  }

  private get currentUserIsActiveOrgUser() {
    return this.project?.organisationUser?.status === OtUserStatus.Active;
  }
  private get currentUserIsActiveProjectUser() {
    return this.project?.projectUser?.status === OtUserStatus.Active;
  }
  private get projectIsSuspended() {
    return this.project?.status === OtProjectStatus.Suspended;
  }
  private get projectIsComplete() {
    return this.project?.status === OtProjectStatus.Complete;
  }

  private get disableContextButton() {
    // Suspended/Complete projects cannot have contracts added so if the project is in one of those states, disable the button
    switch (this.primaryContextBtn) {
      case this.newContractButton:
      case this.newClaimButton:
      case this.requestContractButton:
        return this.projectIsSuspended || this.projectIsComplete;
      case this.newDocumentButton:
        return (
          this.projectIsSuspended ||
          this.projectIsComplete ||
          !(this.currentUserIsActiveOrgUser || this.currentUserIsActiveProjectUser)
        );
      default:
        return false;
    }
  }

  // * WATCHERS
  @Watch('routePath')
  private async checkRoutePath() {
    this.loading = true;

    if (!this.project || !areSame(this.project.gid, this.projectGid)) {
      await this.handleReloadProjectDetails();
    }
    // contracts
    if (this.$route.name === ROUTE_PROJECT_CONTRACTS && !this.contracts.length) {
      await this.handleReloadContracts();
    }
    // projectUsers
    if (this.$route.name === ROUTE_PROJECT_USERS && !this.projectUsers.length) {
      await this.handleReloadProjectUsers();
    }
    // claims
    if (this.$route.name === ROUTE_PROJECT_WORKFLOWS && !this.segmentInstances.length) {
      await this.handleReloadSegmentInstances();
    }
    this.loading = false;
  }

  // * METHODS

  setProjectNameToBreadcrumb() {
    vxm.breadcrumbs.setProjectName({ projectName: this.projectName });
  }

  updateProject(projectGid: string) {
    this.$router.push({ name: this.currentRouteName || ROUTE_PROJECT, params: { projectGid } });
  }

  private async handleReloadProjectDetails(callback?: () => void) {
    const result = await executeApi(
      () => this.api.projects().getProjectDetails(this.projectGid),
      'Load Project Details',
    );
    if (result.success && result.data) {
      this.project = ProjectDetails.createFromApiResponse(result.data);
      vxm.breadcrumbs.setProjectName({ projectName: this.project.name });
    }
    if (callback) {
      callback();
    }
  }

  private async handleReloadContracts(callback?: () => void) {
    const result = await executeApi(() => this.api.contracts().getContracts(this.projectGid), 'Load Contracts');
    if (result.success && result.data?.contracts) {
      this.contracts = result?.data?.contracts?.map(contract => Contract.createFromApiResponse(contract));
    }
    if (callback) {
      callback();
    }
  }
  private async handleReloadProjectUsers(callback?: () => void) {
    const result = await executeApi(() => this.api.projects().getProjectUsers(this.projectGid), 'Load Users');
    if (result.success && result.data?.users) {
      this.projectUsers = result?.data?.users?.map(user => ProjectUser.createFromApiResponse(user));
    }
    if (callback) {
      callback();
    }
  }

  private async handleReloadSegmentInstances(callback?: () => void) {
    const apiResponse = await executeApi(
      () => this.api.segmentInstances().getSegmentInstances(undefined, this.projectGid),
      'Load Workflows',
    );
    if (apiResponse.success && apiResponse.data?.segmentInstances) {
      this.segmentInstances = apiResponse.data.segmentInstances.map(s => SegmentInstance.createFromApiResponse(s));
    }
    if (callback) {
      callback();
    }
  }

  private getTagStatus(status: OtProjectStatus): TagStatus {
    return { type: OtStatusType.Project, status };
  }

  @Ref('inviteUserDialogRef')
  private readonly inviteUserDialogRef!: OtInviteUserDialog;

  private async createNewUser() {
    if (!this.project) return;

    const params = new InviteUserDialogParams({
      isReinvite: false,
      organisationGid: this.project.organisationGid,
      inviteToOrganisation: false,
      projects: [this.project.gid],
    });

    const dialogResult = await this.inviteUserDialogRef.open(params);
    if (dialogResult) {
      this.handleReloadProjectUsers();
    }
  }

  private dodgyHackToReloadCurrentView = 1;

  private async createNewDocument() {
    const params = new AddEditDocumentDialogParams({
      addToOrganisation: false,
      targetOrganisationGid: this.project?.organisationGid,
      addToProjectGids: [this.projectGid],
      context: 'Project',
      mode: AddEditDialogMode.Create,
    });
    const result = await vxm.modal.openAddEditDocumentDialog(params);
    if (result.ok) {
      // the documents tab does its own loading. We can't just call handleReloadDocuments here, because it doesn't exist
      // we could somehow pulse a signal down (update a Symbol) that could be watched, but meh
      // we could allow the child to register an onReloadDocuments handler by emitting a setReloadDocuments event, hold it, call it here, but meh
      // incrementing a variable to reload the whole shooting match works well enough.
      // Unfortunately it blows away the search criteria, that might be annoying.
      // If the user is sitting there with a list of 5 documents to create, all with a reference ABC123-something, and they search
      // for ABC-123 to see what needs creating
      // it might be beneficial to retain the search results. If that turns out to be a wanted feature, consider the setReloadDocuments event
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }
  private async importDocument() {
    if (!this.project) return;

    const params = new SelectDocumentDialogParams({
      targetOrganisationGid: this.project?.organisationGid,
      context: 'Project',
    });
    const dialogResult = await vxm.modal.openSelectDocumentDialog(params);
    if (dialogResult.ok) {
      for (const selectedDocument of dialogResult.selectedDocuments) {
        const request = new PostUpdateDocumentAssociationsRequestModel({
          addProjectAssociations: [this.projectGid],
          addContractAssociations: [],
          addOrganisationAssociations: [],
          removeProjectAssociations: [],
          removeContractAssociations: [],
          removeOrganisationAssociations: [],
        });
        await executeApi(
          () => this.api.documents().postUpdateDocumentAssociations(selectedDocument.gid, request, undefined),
          'Associate Document with Project',
        );
      }
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }
  private async importFromAconex() {
    const params = new AconexImportDialogParams({
      documentParams: new AddEditDocumentDialogParams({
        addToOrganisation: false,
        targetOrganisationGid: this.project?.organisationGid,
        addToProjectGids: [this.projectGid],
        context: 'Project',
        mode: AddEditDialogMode.Create,
      }),
    });
    const result = await doAconexImport(params);
    if (result.didImport) {
      this.dodgyHackToReloadCurrentView = this.dodgyHackToReloadCurrentView + 1;
    }
  }

  // * LIFECYCLE
  private created() {
    this.checkRoutePath();
  }
}
